import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useVocab } from "@/lib/vocab";

const HERO = "https://images.pexels.com/photos/27692895/pexels-photo-27692895.jpeg";

export default function Register() {
  const { register } = useAuth();
  const voc = useVocab();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", access_code: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const brand = settings?.company_name || "Fiableo";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await register(form); nav("/"); }
    catch (e2) { setErr(apiError(e2.response?.data?.detail) || e2.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block grain">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
            {settings?.logo_url && <img src={`${API}/branding/logo`} alt="" className="h-9 w-9 rounded-lg object-contain bg-white/10 p-0.5" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            {brand}
          </div>
          <div>
            <div className="text-xs uppercase tracking-overline text-accent mb-4">Rejoignez l'équipe</div>
            <h1 className="font-display font-black text-4xl xl:text-5xl leading-[1.05]">Créez votre accès en quelques secondes.</h1>
            <p className="mt-6 text-primary-foreground/70 max-w-md">Un code d'accès vous est fourni par votre administrateur.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden font-display font-black text-2xl mb-8">{brand}</div>
          <h2 className="font-display font-bold text-3xl tracking-tight">Créer un compte</h2>
          <p className="text-muted-foreground mt-2 text-sm">{`Votre compte sera créé avec le rôle ${voc.Employee}.`}</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="register-name" placeholder="Jean Dupont" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email" placeholder="vous@exemple.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <PasswordInput id="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} placeholder="6 caractères min." testid="register-password" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Code d'accès</Label>
              <Input id="code" value={form.access_code} onChange={(e) => set("access_code", e.target.value)} data-testid="register-code" placeholder="Code fourni par l'administrateur" />
            </div>
            {err && <p className="text-sm text-destructive" data-testid="register-error">{err}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">{loading ? "…" : "Créer mon compte"}</Button>
            <p className="text-sm text-center text-muted-foreground">
              Déjà un compte ?{" "}
              <button type="button" className="text-accent font-medium hover:underline" onClick={() => nav("/login")} data-testid="go-login">Se connecter</button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
